import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import {
  Card,
  Table,
  Stack,
  Button,
  TableBody,
  Container,
  IconButton,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  MenuItem,
  Box,
  Typography,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useSnackbar } from "notistack";
// routes
import { PATH_DASHBOARD } from "../../../routes/paths";
// @types
import { IRecall } from "../../../@types/recalls";
// components
import Iconify from "../../../components/iconify";
import Scrollbar from "../../../components/scrollbar";
import CustomBreadcrumbs from "../../../components/custom-breadcrumbs";
import { useSettingsContext } from "../../../components/settings";
import CustomCampaignsDialog from "../../../components/custom-campaigns-dialog";
import SendRecallsDialog from "../../../components/send-recalls-dialog";
import MenuPopover from "../../../components/menu-popover/MenuPopover";
import {
  useTable,
  emptyRows,
  TableNoData,
  TableEmptyRows,
} from "../../../components/table";
// sections
import {
  RecallTableRow,
  RecallTableToolbar,
  RecallAnalytics,
} from "../../../sections/@dashboard/recalls";
import EditRecallDialog from "../../../sections/@dashboard/recalls/EditRecallDialog";
import { RecallsService } from "../../../api/recalls.service";

const TABLE_HEAD = [
  { id: "patientName", label: "Patient Name", align: "left" },
  { id: "communicationType", label: "Communication Type", align: "center" },
  { id: "recallType", label: "Recall Type", align: "center" },
  { id: "stage", label: "Stage", align: "center" },
  { id: "status", label: "Status", align: "center" },
  { id: "typeOfAppointment", label: "Type of Appointment", align: "center" },
  { id: "lastExamDate", label: "Last Exam Date", align: "center" },
  { id: "recallDate", label: "Recall Date", align: "center" },
  { id: "actions", label: "Actions", align: "center" },
];

// ----------------------------------------------------------------------

export default function RecallsPage() {
  const { themeStretch } = useSettingsContext();
  const { enqueueSnackbar } = useSnackbar();

  const {
    dense,
    page,
    rowsPerPage,
    setPage,
    selected,
    onSelectRow,
    onSelectAllRows,
  } = useTable({ defaultOrderBy: "recallDate" });

  const [recalls, setRecalls] = useState<IRecall[]>([]);
  const [originalRecalls, setOriginalRecalls] = useState<IRecall[]>([]);

  const [filterName, setFilterName] = useState("");
  const [statusValues, setStatusValues] = useState<
    { label: string; value: string }[]
  >([]);
  const [customCampaignsOpen, setCustomCampaignsOpen] = useState(false);
  const [sendRecallsOpen, setSendRecallsOpen] = useState(false);
  const [editRecallOpen, setEditRecallOpen] = useState(false);
  const [selectedRecall, setSelectedRecall] = useState<IRecall | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [filterStage, setFilterStage] = useState("all");
  const [sendAllLoading, setSendAllLoading] = useState(false);
  const [sendingRowId, setSendingRowId] = useState<string | null>(null);
  const denseHeight = dense ? 56 : 76;

  const isFiltered = filterName !== "" || statusValues.length > 0;

  const isNotFound =
    (!recalls.length && !!filterName) || (!recalls.length && isFiltered);

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  useEffect(() => {
    let filteredRecalls = originalRecalls;

    if (filterName) {
      const lowerName = filterName.toLowerCase();
      filteredRecalls = filteredRecalls.filter((recall: IRecall) =>
        recall.patientName?.toLowerCase().includes(lowerName),
      );
    }

    if (statusValues.length > 0) {
      const selectedStatuses = statusValues.map((v) => v.value.toLowerCase());
      filteredRecalls = filteredRecalls.filter((recall: any) => {
        const status = recall.status
          ? recall.status.toLowerCase()
          : "scheduled";

        if (selectedStatuses.includes("pending") && status === "scheduled")
          return true;

        return selectedStatuses.includes(status);
      });
    }

    if (filterStage !== "all") {
      filteredRecalls = filteredRecalls.filter(
        (recall) => recall.stage === Number(filterStage),
      );
    }

    if (selectedDate) {
      const filterDateStr = selectedDate.toISOString().split("T")[0];
      filteredRecalls = filteredRecalls.filter((recall) => {
        const recallDateStr = new Date(recall.recallDate)
          .toISOString()
          .split("T")[0];
        return recallDateStr <= filterDateStr;
      });
    }

    setRecalls(filteredRecalls);
  }, [filterName, statusValues, filterStage, selectedDate, originalRecalls]);

  const handleStatusFilter = (options: { label: string; value: string }[]) => {
    setPage(0);
    setStatusValues(options);
  };

  const handleFilterStage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setFilterStage(event.target.value);
  };

  const handleFilterStageTab = (
    event: React.SyntheticEvent,
    newValue: string,
  ) => {
    setPage(0);
    setFilterStage(newValue);
  };

  const handleDeleteRow = async (id: string) => {
    try {
      const recallsService = new RecallsService();
      await recallsService.deleteRecall(Number(id));

      const updatedRecalls = recalls.filter((row) => row.id !== id);
      setRecalls(updatedRecalls);

      if (page > 0 && updatedRecalls.length < page * rowsPerPage) {
        setPage(page - 1);
      }
    } catch (error) {
      console.error("Failed to delete recall", error);
    }
  };

  const getErrorMessage = (error: any) => {
    const msg = error?.response?.data?.message;
    if (Array.isArray(msg)) return msg[0] || "Failed to send recall.";
    return msg || "Failed to send recall.";
  };

  const handleSendRow = async (id: string) => {
    try {
      setSendingRowId(id);
      const recallsService = new RecallsService();
      await recallsService.sendRecall(Number(id));
      enqueueSnackbar("Recall sent successfully!");
      getAllRecalls();
    } catch (error: any) {
      console.error("Failed to send recall", error);
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
       setSendingRowId(null);
    }
  };

  const handleResetFilter = () => {
    setFilterName("");
    setStatusValues([]);
    setFilterStage("all");
    setSelectedDate(null);
    setRecalls(originalRecalls);
  };

  const handleEditRecall = (recall: IRecall) => {
    setSelectedRecall(recall);
    setEditRecallOpen(true);
  };

  const handleEditRecallClose = () => {
    setEditRecallOpen(false);
    setSelectedRecall(null);
  };

  const handleEditRecallSave = async (updatedRecall: Partial<IRecall>) => {
    try {
      const recallsService = new RecallsService();
      
      const effectiveCommunicationType =
        updatedRecall.communicationType || selectedRecall!.communicationType;

      const updatedStage =
        effectiveCommunicationType === "SMS"
          ? 1
          : effectiveCommunicationType === "Email"
          ? 2
          : 3;

      await recallsService.updateRecall(selectedRecall!.id, {
        ...updatedRecall,
        communicationType: effectiveCommunicationType,
        stage: updatedStage,
      });
      enqueueSnackbar("Recall updated successfully!");
      handleEditRecallClose();
      getAllRecalls();
    } catch (error) {
      console.error("Failed to update recall", error);
      enqueueSnackbar("Failed to update recall", { variant: "error" });
    }
  };

  const handleCustomCampaignsOpen = () => {
    setCustomCampaignsOpen(true);
  };

  const handleCustomCampaignsClose = () => {
    setCustomCampaignsOpen(false);
  };

  const handleSendRecallsOpen = () => {
    setSendRecallsOpen(true);
  };

  const handleSendRecallsClose = () => {
    setSendRecallsOpen(false);
  };

  const handleDateChange = (newDate: Date | null) => {
    setSelectedDate(newDate);
  };

  const getAllRecalls = async () => {
    try {
      const recallsService = new RecallsService();
      const response = await recallsService.getRecalls();
      const data = response.data;

      const mappedData = data.map((recall: any) => ({
        id: recall.id,
        communicationType: recall.communicationType,
        recallType: recall.recallType,
        stage: recall.stage,
        typeOfAppointment: recall.recallType,
        recallDate: recall.scheduledDate,
        lastExamDate: recall.patient?.lastVisit,
        patientName: `${recall.patient?.firstName || ""} ${recall.patient?.surName || ""}`,
        status: recall.status,
        scheduledDate: recall.scheduledDate,
        message: recall.message,
      }));

      setRecalls(mappedData);
      setOriginalRecalls(mappedData);
    } catch (error) {
      console.error("Failed to fetch recalls", error);
    }
  };

  const handleSendAllDue = async () => {
    try {
      setSendAllLoading(true);
      const recallsService = new RecallsService();
      let communicationType: string | undefined;

      if (filterStage === "1") communicationType = "SMS";
      if (filterStage === "2") communicationType = "Email";
      if (filterStage === "3") communicationType = "Phone Call";

      const res = await recallsService.sendAllRecalls(communicationType);

      const { totalFound, processed, skipped } = res.data;

      if (!totalFound) {
        enqueueSnackbar(
          "No scheduled recalls available to send (future dated or already sent).",
          { variant: "info" },
        );
        return;
      }

      if (processed === 0) {
        enqueueSnackbar("Recalls found but none were eligible to send.", {
          variant: "warning",
        });
        return;
      }

      enqueueSnackbar(
        `${processed} recall(s) sent successfully. ${
          skipped ? `${skipped} skipped.` : ""
        }`,
        { variant: "success" },
      );

      recallsService.getRecalls();
    } catch (error: any) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setSendAllLoading(false);
    }
  };

  // ----------------------------------------------------------------------
  // SYSTEM JOBS (DEV ONLY)
  // ----------------------------------------------------------------------
  const [systemJobsAnchorEl, setSystemJobsAnchorEl] =
    useState<null | HTMLElement>(null);
  const openSystemJobs = Boolean(systemJobsAnchorEl);

  const handleSystemJobsClick = (event: React.MouseEvent<HTMLElement>) => {
    setSystemJobsAnchorEl(event.currentTarget);
  };

  const handleSystemJobsClose = () => {
    setSystemJobsAnchorEl(null);
  };

  const handleRunDailyStaging = async () => {
    try {
      const recallsService = new RecallsService();
      await recallsService.triggerDailyStaging();
      enqueueSnackbar("Daily Staging Job triggered successfully!");
      handleSystemJobsClose();
      getAllRecalls(); // Refresh list to see changes
    } catch (error) {
      console.error("Failed to run daily staging", error);
      enqueueSnackbar("Failed to run daily staging", { variant: "error" });
    }
  };

  const handleRunSafetyNet = async () => {
    try {
      const recallsService = new RecallsService();
      await recallsService.triggerSafetyNet();
      enqueueSnackbar("Safety Net Scan triggered successfully!");
      handleSystemJobsClose();
      getAllRecalls();
    } catch (error) {
      console.error("Failed to run safety net", error);
      enqueueSnackbar("Failed to run safety net", { variant: "error" });
    }
  };

  const handleRunDailyReminders = async () => {
    try {
      // Lazy import or use existing service pattern
      const { AppointmentService } =
        await import("../../../api/appointment.service");
      const appointmentService = new AppointmentService();
      await appointmentService.triggerDailyReminders();
      enqueueSnackbar("Daily Appointment Reminders triggered successfully!");
      handleSystemJobsClose();
    } catch (error) {
      console.error("Failed to run daily reminders", error);
      enqueueSnackbar("Failed to run daily reminders", { variant: "error" });
    }
  };

  useEffect(() => {
    getAllRecalls();
  }, []);

  return (
    <>
      <Helmet>
        <title>Recalls | OptoPro</title>
      </Helmet>

      <Container maxWidth={themeStretch ? false : "xl"}>
        <CustomBreadcrumbs
          heading="Recalls"
          links={[
            {
              name: "Dashboard",
              href: PATH_DASHBOARD.root,
            },
            {
              name: "Recalls",
            },
          ]}
          action={
            <Stack direction="row" spacing={1}>
              {/* <Button
                variant="outlined"
                color="info"
                onClick={handleSystemJobsClick}
                startIcon={<Iconify icon="eva:settings-2-fill" />}
              >
                System Jobs
              </Button> */}
              <Button
                variant="outlined"
                color="primary"
                onClick={handleCustomCampaignsOpen}
              >
                Custom Campaigns
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleSendRecallsOpen}
              >
                Send Recall
              </Button>

              <MenuPopover
                open={systemJobsAnchorEl}
                onClose={handleSystemJobsClose}
                sx={{ width: 220, p: 0 }}
              >
                <Box sx={{ p: 2, pb: 1 }}>
                  <Typography variant="subtitle1">System CRON Jobs</Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    DEV TOOLS - Remove in Prod
                  </Typography>
                </Box>

                <MenuItem onClick={handleRunDailyStaging} sx={{ m: 1 }}>
                  <Iconify icon="eva:layers-fill" sx={{ mr: 1 }} />
                  Run Daily Staging
                </MenuItem>

                <MenuItem onClick={handleRunSafetyNet} sx={{ m: 1 }}>
                  <Iconify icon="eva:shield-fill" sx={{ mr: 1 }} />
                  Run Safety Net
                </MenuItem>

                <MenuItem onClick={handleRunDailyReminders} sx={{ m: 1 }}>
                  <Iconify icon="eva:bell-fill" sx={{ mr: 1 }} />
                  Run Appt Reminders
                </MenuItem>
              </MenuPopover>

              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:pie-chart-2-fill" />}
                onClick={() => setShowAnalytics(!showAnalytics)}
              >
                {showAnalytics ? "Show List" : "Analytics"}
              </Button>

              <Button
                variant="contained"
                disabled={sendAllLoading}
                startIcon={
                  sendAllLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : null
                }
                onClick={handleSendAllDue}
              >
                {sendAllLoading ? "Sending..." : "Send All"}
              </Button>
              <IconButton onClick={getAllRecalls}>
                <Iconify icon="eva:refresh-fill" />
              </IconButton>
            </Stack>
          }
        />

        {showAnalytics ? (
          <RecallAnalytics />
        ) : (
          <Card>
            <Tabs
              value={filterStage}
              onChange={handleFilterStageTab}
              sx={{
                px: 2,
                bgcolor: "background.neutral",
              }}
            >
              {[
                { value: "all", label: "All" },
                { value: "1", label: "Stage 1 (SMS)" },
                { value: "2", label: "Stage 2 (Email)" },
                { value: "3", label: "Stage 3 (Call List)" },
              ].map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={tab.label}
                  sx={{ textTransform: "capitalize" }}
                />
              ))}
            </Tabs>

            <Divider />

            <RecallTableToolbar
              isFiltered={isFiltered}
              filterName={filterName}
              selectedDate={selectedDate}
              filterStage={filterStage}
              recalls={recalls}
              onFilterName={handleFilterName}
              onFilterStage={handleFilterStage}
              onDateChange={handleDateChange}
              onResetFilter={handleResetFilter}
            />

            <TableContainer sx={{ position: "relative", overflow: "unset" }}>
              <Scrollbar>
                <Table size={dense ? "small" : "medium"} sx={{ minWidth: 800 }}>
                  <TableHead>
                    <TableRow>
                      {TABLE_HEAD.map((headCell) => (
                        <TableCell
                          key={headCell.id}
                          align={
                            (headCell.align || "center") as
                              | "center"
                              | "right"
                              | "left"
                              | "inherit"
                              | "justify"
                          }
                        >
                          {headCell.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {recalls
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((row) => (
                        <RecallTableRow
                          key={row.id}
                          row={row}
                          loading={sendingRowId === row.id}
                          onDeleteRow={() => handleDeleteRow(row.id)}
                          onSendRow={() => handleSendRow(row.id)}
                          onEditRow={() => handleEditRecall(row)}
                        />
                      ))}

                    <TableEmptyRows
                      height={denseHeight}
                      emptyRows={emptyRows(page, rowsPerPage, recalls.length)}
                    />

                    <TableNoData isNotFound={isNotFound} />
                  </TableBody>
                </Table>
              </Scrollbar>
            </TableContainer>
          </Card>
        )}
      </Container>

      <CustomCampaignsDialog
        open={customCampaignsOpen}
        onClose={handleCustomCampaignsClose}
      />

      <SendRecallsDialog
        open={sendRecallsOpen}
        onClose={handleSendRecallsClose}
      />

      <EditRecallDialog
        open={editRecallOpen}
        recall={selectedRecall}
        onClose={handleEditRecallClose}
        onSave={handleEditRecallSave}
      />
    </>
  );
}
